import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Dropzone from '@/components/ui/scout/upload/Dropzone';
import { Views } from '@/types/view';
import { FilesWithLocations, ScoutLocation } from '@/types/location';
import { useScout } from '@/hooks/useScout';

interface Props {
  setShowModal: Dispatch<SetStateAction<boolean>>;
  filesWithLocations: FilesWithLocations;
  setFilesWithLocations: Dispatch<SetStateAction<FilesWithLocations>>;
  setActiveView: Dispatch<SetStateAction<Views>>;
}

const Upload: React.FC<Props> = ({
  setShowModal,
  filesWithLocations,
  setFilesWithLocations,
  setActiveView,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = useState(false);

  const { setLocations } = useScout();

  const handleParsedLocations = (
    locations: ScoutLocation[],
    mode: 'add' | 'delete',
    hasFailures: boolean,
  ) => {
    if (mode === 'add') {
      setLocations(prevState => [...locations, ...prevState]);
    } else {
      setLocations(prevState => {
        const copy = [...prevState];
        locations.forEach(location => {
          const index = copy.findIndex(loc => loc._id === location._id);
          copy.splice(index, 1);
        });
        return copy;
      });
    }

    if (!hasFailures) {
      setShowModal(false);
    }

    setActiveView(prevState => {
      if (prevState === Views.Location) {
        return Views.List;
      }

      return prevState;
    });
  };

  const eventKeyCallback = useCallback(
    (event: any) => {
      if (isLoading) {
        return;
      }

      switch (event.key) {
        case 'Escape':
          setShowModal(false);
          break;
      }
    },
    [setShowModal, isLoading],
  );

  const handleClickOutside = useCallback(
    (event: any) => {
      if (
        !isLoading &&
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setShowModal(false);
      }
    },
    [setShowModal, isLoading],
  );

  useEffect(() => {
    document.addEventListener('keydown', eventKeyCallback, false);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', eventKeyCallback, false);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [eventKeyCallback, isLoading, handleClickOutside]);

  return (
    <div ref={modalRef} className="w-full">
      <Dropzone
        callback={handleParsedLocations}
        filesWithLocations={filesWithLocations}
        setFilesWithLocations={setFilesWithLocations}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        setActiveView={setActiveView}
        setShowModal={setShowModal}
      />
    </div>
  );
};

export default Upload;
